import { z } from 'zod';
import { IdSchema, InstantSchema } from './common.ts';

/**
 * What a notification points at.
 *
 * Kind and ids rather than a path, so a route can be built without the
 * storage layout leaking into the UI — and so a record that moves does not
 * strand every notification about it.
 */
export const RecordRefSchema = z.object({
  kind: z.enum([
    'scheduleItem',
    'job',
    'dailyLog',
    'todo',
    'file',
    'comment',
    'receipt',
    'bill',
    'safetyNotice',
    /**
     * Added for RFIs (P8-11), which point at the record they are about.
     *
     * Additive and needing no migration: every stored `RecordRef` still
     * holds a valid kind. `NOTIFIABLE_RECORDS` in `notifications/events.ts`
     * is a **separate**, narrower list and is deliberately not widened —
     * nothing notifies about a change order or a selection through this
     * path, and adding a kind here does not add an event.
     */
    'changeOrder',
    'selection',
    /**
     * Added for attachments (P8-12a), and for the reason the module's
     * one-line spec gives: a homeowner describing a stain is worth much
     * less than a photograph of it, and a spec sheet that cannot carry
     * the drawing it refers to is half a sheet.
     *
     * Additive like the two above and needing no migration — every
     * stored `RecordRef` still holds a valid kind. And like them, this
     * adds **no notification**: `NOTIFIABLE_RECORDS` is a separate,
     * narrower list and widening this one does not widen that.
     */
    'warrantyClaim',
    'specSection',
  ]),
  id: IdSchema,
  /**
   * Present for anything that lives under a job, which is most of it.
   *
   * A **safety notice has none**: it is company-wide. Both route builders
   * already handle that — `linkFor` sends the email to `/app` and
   * `routeFor` declines to navigate — and `/app` is where the splash is
   * waiting, so the chase closes its own loop.
   */
  jobId: IdSchema.optional(),
});
export type RecordRef = z.infer<typeof RecordRefSchema>;

/**
 * What to call each kind of record in front of a person.
 *
 * Here rather than in a component because three screens now name these —
 * a to-do's links, the portal's recent comments, and notifications — and
 * three copies is three chances for one of them to say "dailyLog".
 */
export const RECORD_KIND_LABEL: Record<RecordRef['kind'], string> = {
  scheduleItem: 'Schedule item',
  job: 'Job',
  dailyLog: 'Daily log',
  todo: 'To-do',
  file: 'File',
  comment: 'Comment',
  receipt: 'Receipt',
  bill: 'Bill',
  safetyNotice: 'Safety notice',
  changeOrder: 'Change order',
  selection: 'Selection',
  warrantyClaim: 'Warranty claim',
  specSection: 'Specification section',
};

/**
 * One notification, for one person.
 *
 * Lives at `companies/{c}/notifications/{uid}/items/{n}` — the recipient is
 * in the path rather than in a field, because Firestore rules cannot
 * restrict a `list` by field value.
 *
 * `title` and `body` are stored rather than derived at read time. A
 * notification is a record of what somebody was told, and re-deriving it
 * later from the current state would quietly rewrite history: "dates moved
 * to the 19th" must not become "to the 26th" because they moved again.
 */
export const NotificationSchema = z.object({
  id: IdSchema,
  companyId: IdSchema,
  /** Who it is for. Duplicated from the path so a read is self-describing. */
  recipientUid: IdSchema,
  /** A key from `NOTIFICATION_EVENTS`. */
  event: z.string().min(1),
  title: z.string().min(1).max(200),
  body: z.string().max(1000).optional(),
  recordRef: RecordRefSchema,
  /** Who caused it, for "Dana accepted" rather than "an answer arrived". */
  actorUid: IdSchema.optional(),
  actorName: z.string().max(200).optional(),
  at: InstantSchema,
  /** Absent until opened. A timestamp rather than a flag, so "when" survives. */
  readAt: InstantSchema.optional(),
  /**
   * Channels this was actually delivered on.
   *
   * Recorded because "why didn't I get an email" is a real support question
   * and the preference that suppressed it may have changed since.
   */
  channels: z.array(z.enum(['inApp', 'email', 'push'])).default([]),
});
export type Notification = z.infer<typeof NotificationSchema>;
